import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Play, Pause, FilmStrip, DownloadSimple, ShareNetwork, SkipForward, SkipBack, SpeakerHigh, SpeakerSlash } from '@phosphor-icons/react';
import BackButton from '../components/BackButton';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FILM_TYPES = [
  { id: 'full', name: 'Full Film', desc: '3-10 minutes - Every key moment', icon: '\u{1F3AC}', color: '#B89762' },
  { id: 'highlight', name: 'Highlight Reel', desc: '30-60 seconds - Best moments', icon: '\u{1F31F}', color: '#C29D52' },
  { id: 'social', name: 'Social Clip', desc: '15-30 seconds - Share anywhere', icon: '\u{1F4F1}', color: '#FF0099' },
];

const MOMENT_LABELS = ['Arrival', 'Getting Ready', 'Ceremony', 'Celebration', 'Speeches', 'Dance Floor', 'Party Time', 'Late Night'];

const PocketFilm = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [step, setStep] = useState('choose'); // choose | generating | preview | error
  const [filmType, setFilmType] = useState(null);
  const [progress, setProgress] = useState(0);
  const [filmId, setFilmId] = useState(null);
  const [filmData, setFilmData] = useState(null);
  const [eventName, setEventName] = useState('');
  const [galleryCount, setGalleryCount] = useState(0);
  const [errorMsg, setErrorMsg] = useState('');
  const videoRef = useRef(null);
  const pollRef = useRef(null);

  // Load event info + gallery count
  useEffect(() => {
    fetch(`${API}/events/slug/${slug}`).then(r => r.json()).then(d => {
      setEventName(d.event_name || slug);
    }).catch(() => setEventName(slug));

    fetch(`${API}/pocket-booth/${slug}/gallery`).then(r => r.json()).then(d => {
      // Gallery returns {items: [...]} - count the items array
      const items = d.items || d || [];
      setGalleryCount(Array.isArray(items) ? items.length : 0);
    }).catch(() => {});
  }, [slug]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  // Start film generation
  const generateFilm = async (type) => {
    setFilmType(type);
    setStep('generating');
    setProgress(0);
    setErrorMsg('');

    try {
      const res = await fetch(`${API}/pocket-booth/${slug}/film/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type })
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setErrorMsg(err.detail || 'Failed to start film generation');
        setStep('error');
        return;
      }

      const data = await res.json();
      if (!data.film_id) {
        setErrorMsg('No film ID returned');
        setStep('error');
        return;
      }

      setFilmId(data.film_id);

      // Poll for progress
      pollRef.current = setInterval(async () => {
        try {
          const statusRes = await fetch(`${API}/pocket-booth/${slug}/film/${data.film_id}`);
          const status = await statusRes.json();
          setProgress(status.progress || 0);

          if (status.status === 'completed') {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setFilmData(status);
            setStep('preview');
          } else if (status.status === 'failed') {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setErrorMsg(status.error || 'Film generation failed');
            setStep('error');
          }
        } catch { /* retry on next interval */ }
      }, 1500);

    } catch (e) {
      setErrorMsg('Network error - please try again');
      setStep('error');
    }
  };

  const downloadFilm = () => {
    if (filmData?.download_url) {
      const url = `${BACKEND_URL}${filmData.download_url}`;
      const a = document.createElement('a');
      a.href = url;
      a.download = `event-film-${filmType}.mp4`;
      a.click();
    }
  };

  // CHOOSE FILM TYPE
  if (step === 'choose') {
    return (
      <div className="min-h-screen bg-[#0F1014] text-white px-6 py-12" data-testid="pocket-film-choose">
        <div className="mb-8">
          <BackButton fallbackHref={`/event/${slug}/pocket-gallery`} label="Gallery" variant="subtle" testId="film-back" />
        </div>

        <div className="max-w-sm mx-auto text-center">
          <div className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}>
            <FilmStrip size={32} weight="bold" className="text-white" />
          </div>
          <h1 className="text-2xl font-black mb-2">Create Your Film</h1>
          <p className="text-gray-400 text-sm mb-8">FFmpeg renders your event into a cinematic video</p>

          <div className="space-y-3">
            {FILM_TYPES.map(ft => (
              <button key={ft.id} onClick={() => generateFilm(ft.id)}
                disabled={galleryCount < 2}
                className={`w-full flex items-center gap-4 p-5 rounded-2xl border transition-all active:scale-[0.98] text-left ${galleryCount < 2 ? 'border-white/5 bg-[#1B1C22]/50 opacity-50 cursor-not-allowed' : 'border-white/10 bg-[#1B1C22] hover:border-[#C29D52]/50'}`}
                data-testid={`film-type-${ft.id}`}>
                <span className="text-3xl">{ft.icon}</span>
                <div className="flex-1">
                  <h3 className="font-bold text-base">{ft.name}</h3>
                  <p className="text-gray-500 text-xs">{ft.desc}</p>
                </div>
                <div className="w-2 h-2 rounded-full" style={{ background: ft.color }} />
              </button>
            ))}
          </div>

          {galleryCount < 2 && (
            <div className="mt-6 p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/20 text-yellow-300 text-sm">
              Need at least 2 booth captures to generate a film. You have {galleryCount}.
            </div>
          )}

          <div className="mt-8 flex items-center justify-center gap-6 text-gray-600 text-xs">
            <span>{galleryCount} clips available</span>
            <span>{MOMENT_LABELS.length} moments detected</span>
          </div>
        </div>
      </div>
    );
  }

  // GENERATING
  if (step === 'generating') {
    const ft = FILM_TYPES.find(f => f.id === filmType);
    return (
      <div className="min-h-screen bg-[#0F1014] text-white flex flex-col items-center justify-center px-6" data-testid="pocket-film-generating">
        <div className="max-w-sm w-full text-center">
          <div className="w-24 h-24 rounded-3xl mx-auto mb-8 flex items-center justify-center relative" style={{ background: `linear-gradient(135deg, ${ft?.color || '#B89762'}, #C29D52)` }}>
            <FilmStrip size={44} weight="bold" className="text-white" />
            <div className="absolute inset-0 rounded-3xl animate-ping opacity-20" style={{ background: ft?.color || '#B89762' }} />
          </div>

          <h2 className="text-xl font-black mb-2">Creating Your {ft?.name}</h2>
          <p className="text-gray-400 text-sm mb-8">FFmpeg is processing your photos into a video...</p>

          <div className="w-full h-2 rounded-full bg-white/10 mb-3 overflow-hidden">
            <div className="h-full rounded-full transition-all duration-700 ease-out" style={{ width: `${progress}%`, background: `linear-gradient(90deg, ${ft?.color || '#B89762'}, #C29D52)` }} />
          </div>
          <p className="text-gray-500 text-xs" data-testid="film-progress">{progress}% — {progress < 20 ? 'Downloading images' : progress < 45 ? 'Preparing frames' : progress < 85 ? 'Rendering video' : progress < 95 ? 'Uploading film' : 'Finalising'}</p>

          <div className="mt-8 space-y-3">
            {[
              { label: 'Downloading from gallery', done: progress >= 20 },
              { label: 'Preparing frames (1080p)', done: progress >= 45 },
              { label: 'FFmpeg rendering', done: progress >= 85 },
              { label: 'Uploading to storage', done: progress >= 95 },
              { label: 'Ready to watch', done: progress >= 100 },
            ].map((s, i) => (
              <div key={i} className={`flex items-center gap-3 text-sm transition-all ${s.done ? 'text-white' : 'text-gray-600'}`}>
                <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold transition-all ${s.done ? 'bg-green-500 text-white' : 'bg-white/5 text-gray-600'}`}>
                  {s.done ? '\u2713' : i + 1}
                </div>
                <span>{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ERROR
  if (step === 'error') {
    return (
      <div className="min-h-screen bg-[#0F1014] text-white flex flex-col items-center justify-center px-6" data-testid="pocket-film-error">
        <div className="max-w-sm w-full text-center">
          <div className="text-5xl mb-4">{'\u26A0\uFE0F'}</div>
          <h2 className="text-xl font-black mb-2">Generation Failed</h2>
          <p className="text-gray-400 text-sm mb-6">{errorMsg}</p>
          <button onClick={() => { setStep('choose'); setFilmType(null); setErrorMsg(''); }}
            className="px-8 py-3 rounded-2xl text-sm font-bold text-white"
            style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
            data-testid="film-retry">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // PREVIEW (real video player)
  if (step === 'preview' && filmData) {
    const ft = FILM_TYPES.find(f => f.id === filmType);
    const videoUrl = filmData.download_url ? `${BACKEND_URL}${filmData.download_url}` : '';

    return (
      <div className="fixed inset-0 bg-black flex flex-col" data-testid="pocket-film-preview">
        {/* Video player */}
        <div className="flex-1 relative overflow-hidden bg-black flex items-center justify-center">
          {videoUrl ? (
            <video
              ref={videoRef}
              src={videoUrl}
              className="w-full h-full object-contain"
              controls
              autoPlay
              playsInline
              data-testid="film-video-player"
            />
          ) : (
            <p className="text-gray-500">No video available</p>
          )}

          {/* Film type badge */}
          <div className="absolute top-6 right-4 z-10">
            <span className="px-3 py-1 rounded-full text-[10px] font-bold text-white" style={{ background: ft?.color || '#B89762' }}>
              {ft?.name}
            </span>
          </div>
        </div>

        {/* Info bar */}
        <div className="bg-[#0F1014] border-t border-white/10 px-6 py-3">
          <div className="flex items-center justify-between text-xs text-gray-400 mb-3">
            <span>{filmData.frames_used || '?'} photos</span>
            <span>{filmData.duration_seconds ? `${Math.round(filmData.duration_seconds)}s` : ''}</span>
            <span>{filmData.file_size_mb ? `${filmData.file_size_mb} MB` : ''}</span>
          </div>
        </div>

        {/* Actions */}
        <div className="bg-[#0F1014] px-6 pb-8 space-y-3 max-w-sm mx-auto w-full">
          <button onClick={downloadFilm}
            className="w-full h-14 rounded-2xl text-base font-bold text-white flex items-center justify-center gap-2 transition-all active:scale-[0.97]"
            style={{ background: `linear-gradient(135deg, ${ft?.color || '#B89762'}, #C29D52)` }}
            data-testid="film-download">
            <DownloadSimple size={20} weight="bold" /> Download {ft?.name}
          </button>
          <button className="w-full h-12 rounded-2xl text-sm font-bold text-white flex items-center justify-center gap-2 border border-white/10 bg-white/5 hover:bg-white/10 transition-all"
            data-testid="film-share">
            <ShareNetwork size={18} weight="bold" /> Share
          </button>
          <button onClick={() => { setStep('choose'); setFilmType(null); setFilmData(null); }}
            className="w-full h-10 text-sm text-gray-600 hover:text-gray-400 transition-all"
            data-testid="film-new">
            Create Different Format
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default PocketFilm;
